import { FC, useEffect, useRef } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { AssigneeDto } from "../../../api/Api";
import { useAssignees } from "../../../features/tradingCounterparties/dataHooks/counterpartyQueries";
import {
  defaultReadinessSigner,
  ReadinessDepartment
} from "./operationalReadiness";

type Props = {
  department: ReadinessDepartment;
  label: string;
  value: AssigneeDto | null;
  onChange: (signer: AssigneeDto | null) => void;
  /** Propose the department's default director once the group has loaded. */
  prefill: boolean;
  disabled?: boolean;
};

const sameEmail = (a: AssigneeDto, b: AssigneeDto) =>
  !!a.email && !!b.email && a.email.toLowerCase() === b.email.toLowerCase();

/**
 * One operational readiness signer, picked from the department's assignee group (the same
 * groups the lifecycle dialogs assign from; the assignee service is shared by every book).
 */
export const ReadinessSignerSelect: FC<Props> = ({
  department,
  label,
  value,
  onChange,
  prefill,
  disabled = false
}) => {
  const { data, isLoading } = useAssignees(department);
  const members = data ?? [];

  // Once only, so clearing the field does not bring the default straight back.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!prefill || prefilled.current || value || !data) return;
    prefilled.current = true;
    const signer = defaultReadinessSigner(department, data);
    if (signer) onChange(signer);
  }, [prefill, value, data, department, onChange]);

  // A stored signer who has since left the group must still render as the selected value.
  const options =
    value && !members.some((m) => sameEmail(m, value))
      ? [value, ...members]
      : members;

  return (
    <Autocomplete<AssigneeDto>
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(option) => option.name || option.email || ""}
      isOptionEqualToValue={sameEmail}
      loading={isLoading}
      disabled={disabled}
      renderOption={(props, option) => (
        <li {...props} key={option.email ?? option.name}>
          <Box display="flex" flexDirection="column" minWidth={0}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
            {option.email && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.email}
              </Typography>
            )}
          </Box>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} required />}
    />
  );
};
